import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { StationItemNo } from '../../../../constants/Games/gameStation';
import { getStationVisual } from '../../../../constants/Games/gameStationUtils';
import { getGameTypeDetails } from '../../../../constants/Games/gameType';
import { NOT_APPLICABLE_STRING } from '../../../../constants/general';
import { IconType, Icons } from '../../../../constants/icons';
import { SVGIconType } from '../../../../constants/svgIcon';
import { Game } from '../../../../models/GameStore/Game';
import { Space } from '../../../../models/SpaceStore';
import { Profile } from '../../../../models/UserStore';

import { AvatarNode } from '../../AvatarNode';
import { FillPNGIcon } from '../../FillPNGIcon';
import { PngIcon } from '../../PngIcon';
import { SeparatorComponent } from '../../SeparatorComponent';
import { SvgIcon } from '../../SVGIcon';

import './GameStationCell.css';

export type GameStationCellProps = {
  albumClassroom: Space;
  game: Game;
  stationId: StationItemNo;
  name: string | undefined;
  index: number;
  iconKey: string;
  playCount: number;
  averageScore: number;
  remainingJCoinReward: number | undefined;
  remainingSpaceReward: number | undefined;
  userProfile: Profile;
  recordScore: number | undefined;
  recordDuration: string | undefined;
  isUnlocked: boolean;
  onClickPlay: () => void;
  onClickInfo: () => void;
};

export const GameStationCell = memo((props: GameStationCellProps) => {
  const {
    albumClassroom,
    game,
    stationId,
    name,
    index,
    iconKey,
    playCount,
    averageScore,
    remainingJCoinReward,
    remainingSpaceReward,
    userProfile,
    recordScore,
    recordDuration,
    isUnlocked,
    onClickPlay,
    onClickInfo,
  } = props;
  const { t } = useTranslation();

  //game data
  const gameTitle =
    game.i18nGameData.resources[game.i18nGameData.defaultLocale]?.title;

  const gameTypeIcon = iconKey ? getGameTypeDetails(iconKey).icon : undefined;
  const gameTypeName = iconKey
    ? getGameTypeDetails(iconKey).displayName
    : undefined;

  return (
    <div className="game-station-cell" onClick={onClickPlay}>
      <div className="centered-container">
        <div className="lower-row">
          <div className="component">
            <div className="station-icon">
              <img
                src={getStationVisual(stationId)?.filePrefix}
                className="large-icon"
              />
              {/* <SvgIcon
                name={SVGIconType.Share}
                className={`small-icon grey-text`}
              /> */}
            </div>
            <div className="text-overlay">{name ?? index}</div>
            {remainingSpaceReward || remainingJCoinReward ? (
              <div className="reward-bar">
                {remainingSpaceReward ? (
                  <div className="reward">
                    <div className="image-stack">
                      <img
                        src={Icons[IconType.SpaceCreditFrame]}
                        className="frame-image"
                      />
                      <img
                        src={albumClassroom?.icon?.publicUrls?.small}
                        className="space-image"
                      />
                    </div>
                    <div className="reward-text">{remainingSpaceReward}</div>
                  </div>
                ) : null}
                {remainingJCoinReward ? (
                  <div className="reward">
                    <PngIcon name="JCoin" className="reward-icon" />
                    <div className="reward-text">{remainingJCoinReward}</div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div
            className={`game-component ${isUnlocked ? 'unlocked' : 'locked'}`}
          >
            <div className="upper-row">
              <div className="text-row">
                {gameTypeIcon && Icons[gameTypeIcon] ? (
                  <FillPNGIcon
                    src={Icons[gameTypeIcon]}
                    color="#ff9500"
                    size={32}
                  />
                ) : null}
                <div className="text-content">
                  <div className="type-title">{t('AI Memory Game')}</div>
                  <div className={`description grey-text`}>
                    {gameTypeName ? t(gameTypeName) : null}
                  </div>
                </div>
              </div>
              <div
                className="info-container"
                onClick={(e) => {
                  e.stopPropagation(); // Stop the event propagation
                  onClickInfo();
                }}
              >
                <SvgIcon
                  name={SVGIconType.GameInstruction}
                  className={`info-icon grey-text`}
                />
              </div>
            </div>
            <div className="upper-part">
              <div className="left-part">
                <div className="title">{gameTitle}</div>
                <div className="info">
                  <SeparatorComponent
                    separator={<p className={`separate-text grey-text`}>·</p>}
                  >
                    <div className={`reward-text grey-text`}>
                      {t('{count} Plays', {
                        count: playCount,
                      })}
                    </div>
                    <div className="reward">
                      <SvgIcon
                        name={SVGIconType.Score}
                        className={`reward-icon grey-text`}
                      />
                      <div className={`reward-text grey-text`}>
                        {Math.floor(averageScore)}
                      </div>
                    </div>
                  </SeparatorComponent>
                </div>
              </div>

              <div className="right-part">
                {/* <SvgIcon
                  name={SVGIconType.More}
                  className={`more-icon grey-text`}
                /> */}
                {isUnlocked ? (
                  <div
                    className="play-button"
                    onClick={(e) => {
                      e.stopPropagation(); // Stop the event propagation
                      onClickPlay();
                    }}
                  >
                    <SvgIcon
                      name={SVGIconType.Play}
                      width={24}
                      height={24}
                      stroke="#00bfa5"
                    />
                  </div>
                ) : null}
              </div>
            </div>
            {recordScore !== undefined || recordDuration !== undefined ? (
              <div className="lower-part">
                <AvatarNode
                  avatar={{
                    ...userProfile.avatar,
                    userId: userProfile.userId,
                    source:
                      userProfile.profilePicture &&
                      userProfile.profilePicture.publicUrls?.small
                        ? {
                            uri: userProfile.profilePicture.publicUrls.small,
                          }
                        : NOT_APPLICABLE_STRING,
                  }}
                  size={20}
                />
                <div className="reward">
                  <SvgIcon
                    name={SVGIconType.Score}
                    className={`reward-icon grey-text`}
                  />
                  <div className={`reward-text grey-text`}>{recordScore}</div>
                </div>
                <div className="reward">
                  <SvgIcon
                    name={SVGIconType.Timer}
                    className={`reward-icon grey-text`}
                  />
                  <div className={`reward-text grey-text`}>
                    {recordDuration}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
});
